<template>
  <div class="schematic col-12 p-1 h-100 common-card">
    <div class="header" style="border: none;">
      <div class="title">
        계통도
      </div>
    </div>
    <div class="bg" style="box-shadow: none;">
      <div class="schematic-container">
        <div class="image-container">
          <img 
            :src="schematicImgSrc" 
            class="schematic-img"
            ref="imgRef"
            @load="onImgLoad"
          />
          <div class="text-overlay">
            <div
              v-for="(item, idx) in contentItems"
              :key="`${item.key}-${idx}`"
              class="data-text"
              :style="getTextStyle(idx)"
            >
              {{ item.label }} {{ item.value }} {{ item.unit }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import pvGif from '@/assets/images/renewal/pv.gif'
import solarheatGif from '@/assets/images/renewal/solarheat.gif'
import geoGif from '@/assets/images/renewal/geo.gif'
import { useTocStore } from '~/store/tocStore';
import { useEquipStore } from '~/store/equipStore';
import moment from 'moment';

const tocStore = useTocStore();
const equipStore = useEquipStore();

const props = defineProps<{
  apiData?: any
}>();

console.log(props.apiData);

const data = computed(() => {
  if (!props.apiData || !Array.isArray(props.apiData)) return null;
  const newSort = props.apiData.sort((a, b) => {
    if(moment(a.receivedDate) > moment(b.receivedDate)){
      return -1;
    }else if(moment(a.receivedDate) < moment(b.receivedDate)){
      return 1;
    } else {
      return 0;
    }
  });
  
  return newSort[0] || null;
});
const cid = computed(() => data.value?.cid || null);

const specInfo = ref<any>(null);

const engSource = computed(() => {
  if (!cid.value) return '';
  const cidStr = String(cid.value);
  const rIdx = cidStr.indexOf('r');
  if (rIdx !== -1 && cidStr.length > rIdx + 2) {
    return cidStr.substr(rIdx + 1, 2);
  } else {
    return cidStr.substr(0, 2);
  }
});

const schematicImages: Record<string, string> = {
  '10': pvGif,
  '20': solarheatGif,
  '30': geoGif,
};

const schematicImgSrc = computed(() => {
  const cid = equipStore.selectEquipInfo?.cid || '';
  if (!cid) return '';
  const cidStr = String(cid);
  const rIdx = cidStr.indexOf('r');
  if (rIdx !== -1 && cidStr.length > rIdx + 2) {
    return schematicImages[cidStr.substr(rIdx + 1, 2)];
  } else {
    return schematicImages[cidStr.substr(0, 2)];
  }
});

// 에너지원별 데이터 정의 (좌표 제거)
const schematicContentMap = computed(() => {
  const isSinglePhase = specInfo.value?.pviPhase === 'single';
  
  return {
    '10': isSinglePhase ? [
      { label: 'PV전압:', key: 'dc_inputv', unit: 'V'},
      { label: 'PV전류:', key: 'dc_inputa', unit: 'A' },
      { label: 'PV출력:', key: 'dc_input_power', unit: 'W' },
      { label: '출력전압:', key: 'ac_gridv', unit: 'V' },
      { label: '출력전류:', key: 'ac_grida', unit: 'A' },
      { label: '현재출력:', key: 'ac_grid_power', unit: 'W' },
    ] : [
      { label: 'PV전압:', key: 'dc_inputv', unit: 'V' },
      { label: 'PV전류:', key: 'dc_inputa', unit: 'A' },
      { label: 'PV출력:', key: 'dc_input_power', unit: 'W' },
      { label: '출력전압:', key: 'ac_gridvr', unit: 'V' },
      { label: '출력전류:', key: 'ac_gridar', unit: 'A' },
      { label: '현재출력:', key: 'ac_grid_power', unit: 'W' },
    ],
    '20': [
      { label: '집열기 입구온도:', key: 'collectorEntranceTemp', unit: '°C' },
      { label: '집열기 출구온도:', key: 'collectorExitTemp', unit: '°C' },
      { label: '축열조 상부온도:', key: 'htank_top_temp', unit: '°C' },
      { label: '축열조 하부온도:', key: 'htank_bottom_temp', unit: '°C' },
      { label: '급탕온도:', key: 'hwaterPipeTemp', unit: '°C' },
      { label: '급수온도:', key: 'waterPipeTemp', unit: '°C' },
    ],
    '30': [
      { label: '축열조 급탕온도:', key: 'htank_hwatersupply_temp', unit: '°C' },
      { label: '축열조 급수온도:', key: 'htank_watersupply_temp', unit: '°C' },
      { label: '냉난방출구온도:', key: 'hp_hc_exit_temp', unit: '°C' },
      { label: '냉난방입구온도:', key: 'hp_hc_ent_temp', unit: '°C' },
      { label: '지열수입구온도:', key: 'hp_geowater_ent_temp', unit: '°C' },
      { label: '지열수출구온도:', key: 'hp_getwater_exit_temp', unit: '°C' },
      { label: '부하측출구온도:', key: 'load_exit_temp', unit: '°C' },
      { label: '부하측입구온도:', key: 'load_ent_temp', unit: '°C' },
    ]
  };
});

// 데이터와 라벨 매칭
const contentItems = computed(() => {
  const eng = engSource.value;
  const contentMap = schematicContentMap.value[eng as keyof typeof schematicContentMap.value] || [];
  const rawData = data.value || {};
  const items = contentMap.map((item: any) => ({
    ...item,
    value: rawData[item.key] !== undefined && rawData[item.key] !== null ? rawData[item.key] : '-'
  }));
  
  // console.log('Content items:', items);
  // console.log('Eng source:', eng);
  // console.log('Raw data:', rawData);
  
  return items;
});

const imgRef = ref<HTMLImageElement|null>(null);

// 텍스트 스타일 계산
function getTextStyle(idx: number): Record<string, string> {
  const eng = engSource.value;
  let left = '10%';
  let top = '10%';
  
  if (eng === '10') {
    const positions = [
      { left: '10%', top: '20%' },
      { left: '10%', top: '27%' },
      { left: '10%', top: '34%' },
      { left: '40%', top: '20%' },
      { left: '40%', top: '27%' },
      { left: '40%', top: '34%' }
    ];
    const pos = positions[idx] || positions[0];
    left = pos.left;
    top = pos.top;
  } else if (eng === '20') {
    const positions = [
      { left: '15%', top: '30%' },
      { left: '45%', top: '30%' },
      { left: '55%', top: '40%' },
      { left: '55%', top: '90%' },
      { left: '70%', top: '55%' },
      { left: '70%', top: '85%' }
    ];
    const pos = positions[idx] || positions[0];
    left = pos.left;
    top = pos.top;
  } else if (eng === '30') {
    const positions = [
      { left: '32%', top: '15%' },
      { left: '58%', top: '15%' },
      { left: '40%', top: '30%' },
      { left: '40%', top: '55%' },
      { left: '15%', top: '40%' },
      { left: '30%', top: '60%' },
      { left: '60%', top: '65%' },
      { left: '60%', top: '35%' },
    ];
    const pos = positions[idx] || positions[0];
    left = pos.left;
    top = pos.top;
  }
  
  return {
    position: 'absolute',
    left,
    top,
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#000',
    zIndex: '10'
  };
}

function onImgLoad() {
  nextTick(() => {
    if (imgRef.value) {
      console.log('Image loaded, updating positions and sizes');
    }
  });
}

// specInfo 비동기 로드
watch(cid, async (newCid) => {
  if (newCid) {
    try {
      specInfo.value = await equipStore.getEquipSpecInfo(newCid) as any;
    } catch (error) {
      console.error('Failed to load spec info:', error);
      specInfo.value = null;
    }
  } else {
    specInfo.value = null;
  }
}, { immediate: true });

onMounted(() => {
  
  const handleResize = () => {
    console.log('Window resized, updating positions and sizes');
  };
  
  window.addEventListener('resize', handleResize);
  
  onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
  });
});

</script>

<style lang="scss" scoped>
@import "@/assets/styles/design-toc/schematic-card.scss";

.schematic-container {
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

.image-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.schematic-img {
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.text-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.data-text {
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #000;
  white-space: nowrap;
}

// 반응형 미디어 쿼리
@media (max-width: 768px) {
  .data-text {
    font-size: 12px !important;
  }
}

@media (max-width: 480px) {
  .data-text {
    font-size: 10px !important;
  }
}
</style>